import { motion } from "framer-motion";

export interface HowItWorksStep {
  number: string;
  title: string;
  description: string;
}

interface HowItWorksSectionProps {
  badge?: string;
  title?: string;
  description?: string;
  steps?: HowItWorksStep[];
}

const defaultSteps: HowItWorksStep[] = [
  {
    number: "1",
    title: "Register Your School",
    description: "Create your school account and configure basic institutional information.",
  },
  {
    number: "2",
    title: "System Setup",
    description: "Our team configures the system, including classes, subjects, teachers, and student data.",
  },
  {
    number: "3",
    title: "Training",
    description: "We train administrators and teachers to ensure they can use the platform confidently.",
  },
  {
    number: "4",
    title: "Ongoing Support",
    description: "Our support team continues to assist your school with updates, guidance, and system improvements.",
  },
];

const HowItWorksSection = ({
  badge = "How it works",
  title = "How the Platform Works",
  description,
  steps = defaultSteps,
}: HowItWorksSectionProps = {}) => (
  <section className="py-24 bg-slate-50 dark:bg-slate-900/50">
    <div className="container mx-auto px-4 lg:px-8 max-w-6xl">
      <div className="text-center mb-16">
        <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider mb-6">
          {badge}
        </span>
        <h2 className="text-3xl md:text-5xl font-heading font-bold text-slate-800 dark:text-slate-100 tracking-tight">
          {title}
        </h2>
        {description && (
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-slate-500 dark:text-slate-400">
            {description}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 relative">
        {steps.map((step) => (
          <motion.div
            key={step.number}
            className="flex flex-col items-center text-center relative z-10"
          >
            <div aria-hidden="true" className="w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-2xl font-bold mb-8 shadow-lg shadow-primary/20">
              {step.number}
            </div>
            <h3 className="text-xl font-bold text-slate-800 dark:text-slate-200 mb-4">
              {step.title}
            </h3>
            <p className="text-slate-500 dark:text-slate-400 leading-relaxed font-medium">
              {step.description}
            </p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export default HowItWorksSection;
